import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !username || !password) {
      toast.error(language === 'zh' ? '请填写所有字段' : 'Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error(language === 'zh' ? '密码至少需要6位' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(email, username, password);
      toast.success(language === 'zh' ? '注册成功' : 'Registration successful');
      navigate('/dashboard');
    } catch (error) {
      let message = error.response?.data?.detail;
      if (typeof message === 'object') {
        message = JSON.stringify(message);
      }
      message = message || (language === 'zh' ? '注册失败' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="register-page">
      <div className="auth-card animate-fade-in-up">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                WPLMS TTM
              </span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('auth.registerTitle')}
            </h1>
            <p className="text-slate-500 mt-1">{t('auth.registerSubtitle')}</p>
          </div>
          <button
            onClick={toggleLanguage}
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors px-2 py-1 rounded"
            data-testid="language-toggle"
          >
            {language === 'zh' ? 'EN' : '中文'}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="username">{t('auth.username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="johndoe"
              className="input-focus-ring"
              data-testid="register-username-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="input-focus-ring"
              data-testid="register-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-focus-ring"
              data-testid="register-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11"
            disabled={loading}
            data-testid="register-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('auth.register')
            )}
          </Button>
        </form>

        <p className="text-center text-slate-500 mt-6">
          {t('auth.hasAccount')}{' '}
          <Link
            to="/login"
            className="text-indigo-600 hover:text-indigo-700 font-medium"
            data-testid="login-link"
          >
            {t('auth.login')}
          </Link>
        </p>
      </div>
    </div>
  );
}
